import { createAction } from 'store/utils';
import {
  ActivateViesFailureAction,
  ActivateViesRequestAction,
  ActivateViesSuccessAction,
  CheckContactFailureAction,
  CheckContactRequestAction,
  CheckContactsFailureAction,
  CheckContactsRequestAction,
  CheckContactsSuccessAction,
  CheckContactSuccessAction,
  DeactivateViesFailureAction,
  DeactivateViesRequestAction,
  DeactivateViesSuccessAction,
  FetchContactFailureAction,
  FetchContactRequestAction,
  FetchViesContactsFailureAction,
  FetchViesContactsRequestAction,
  FetchViesContactsSuccessAction,
  FetchContactSuccessAction,
  FetchViesInfoFailureAction,
  FetchViesInfoRequestAction,
  FetchViesInfoSuccessAction,
  HideContactFailureAction,
  HideContactRequestAction,
  HideContactSuccessAction,
  RemoveViesInfoAction,
  ResetVatCheckerDataAction,
  RefreshContactFailureAction,
  RefreshContactRequestAction,
  RefreshContactsFailureAction,
  RefreshContactsRequestAction,
  RefreshContactsSuccessAction,
  RefreshContactSuccessAction,
} from './types';
import {
  ACTIVATE_VIES_FAILURE,
  ACTIVATE_VIES_REQUEST,
  ACTIVATE_VIES_SUCCESS,
  CHECK_CONTACT_FAILURE,
  CHECK_CONTACT_REQUEST,
  CHECK_CONTACT_SUCCESS,
  CHECK_CONTACTS_FAILURE,
  CHECK_CONTACTS_REQUEST,
  CHECK_CONTACTS_SUCCESS,
  DEACTIVATE_VIES_FAILURE,
  DEACTIVATE_VIES_REQUEST,
  DEACTIVATE_VIES_SUCCESS,
  FETCH_CONTACT_FAILURE,
  FETCH_CONTACT_REQUEST,
  FETCH_CONTACT_SUCCESS,
  FETCH_VIES_CONTACTS_FAILURE,
  FETCH_VIES_CONTACTS_REQUEST,
  FETCH_VIES_CONTACTS_SUCCESS,
  FETCH_VIES_INFO_FAILURE,
  FETCH_VIES_INFO_REQUEST,
  FETCH_VIES_INFO_SUCCESS,
  HIDE_CONTACT_FAILURE,
  HIDE_CONTACT_REQUEST,
  HIDE_CONTACT_SUCCESS,
  REFRESH_CONTACT_FAILURE,
  REFRESH_CONTACT_REQUEST,
  REFRESH_CONTACT_SUCCESS,
  REFRESH_CONTACTS_FAILURE,
  REFRESH_CONTACTS_REQUEST,
  REFRESH_CONTACTS_SUCCESS,
  REMOVE_VIES_INFO,
  RESET_VAT_CHECKER_DATA,
} from './constants';
import { Contact, HideContactData, ViesCheck, ViesInfo } from './models';

// Get Vies info
export const fetchViesInfoRequest = (
  connectionId: string | undefined = undefined,
  immediateRefresh: boolean = false
): FetchViesInfoRequestAction => {
  return createAction(FETCH_VIES_INFO_REQUEST, {
    connectionId,
    immediateRefresh,
  });
};

export const fetchViesInfoSuccess = (
  viesInfos: ViesInfo[]
): FetchViesInfoSuccessAction =>
  createAction(FETCH_VIES_INFO_SUCCESS, { viesInfos });

export const fetchViesInfoFailure = (): FetchViesInfoFailureAction =>
  createAction(FETCH_VIES_INFO_FAILURE);

// Activate Vies
export const activateViesRequest = (
  connectionId: string,
  entityName: string
): ActivateViesRequestAction =>
  createAction(ACTIVATE_VIES_REQUEST, { connectionId, entityName });

export const activateViesSuccess = (
  connectionId: string
): ActivateViesSuccessAction =>
  createAction(ACTIVATE_VIES_SUCCESS, { connectionId });

export const activateViesFailure = (): ActivateViesFailureAction =>
  createAction(ACTIVATE_VIES_FAILURE);

// Deactivate Vies
export const deactivateViesRequest = (
  connectionId: string,
  entityName: string
): DeactivateViesRequestAction =>
  createAction(DEACTIVATE_VIES_REQUEST, { connectionId, entityName });

export const deactivateViesSuccess = (
  connectionId: string
): DeactivateViesSuccessAction =>
  createAction(DEACTIVATE_VIES_SUCCESS, { connectionId });

export const deactivateViesFailure = (): DeactivateViesFailureAction =>
  createAction(DEACTIVATE_VIES_FAILURE);

// Get VIES contacts
export const fetchViesContactsRequest = (
  connectionId: string,
  suppressMessages: boolean = false
): FetchViesContactsRequestAction =>
  createAction(FETCH_VIES_CONTACTS_REQUEST, {
    connectionId,
    suppressMessages,
  });

export const fetchViesContactsSuccess = (
  contacts: Contact[]
): FetchViesContactsSuccessAction =>
  createAction(FETCH_VIES_CONTACTS_SUCCESS, { contacts });

export const fetchViesContactsFailure = (): FetchViesContactsFailureAction =>
  createAction(FETCH_VIES_CONTACTS_FAILURE);

// Get Contact
export const fetchContactRequest = (
  connectionId: string,
  contactUuid: string
): FetchContactRequestAction => {
  return createAction(FETCH_CONTACT_REQUEST, { connectionId, contactUuid });
};

export const fetchContactSuccess = (
  viesChecks: ViesCheck[],
  contact: Contact
): FetchContactSuccessAction =>
  createAction(FETCH_CONTACT_SUCCESS, { viesChecks, contact });

export const fetchContactFailure = (): FetchContactFailureAction =>
  createAction(FETCH_CONTACT_FAILURE);

// Check Contacts
export const checkContactsRequest = (
  connectionId: string
): CheckContactsRequestAction =>
  createAction(CHECK_CONTACTS_REQUEST, { connectionId });

export const checkContactsSuccess = (
  lastViesCheckDate: string,
  contacts: Contact[],
  connectionId: string
): CheckContactsSuccessAction =>
  createAction(CHECK_CONTACTS_SUCCESS, {
    lastViesCheckDate,
    contacts,
    connectionId,
  });

export const checkContactsFailure = (): CheckContactsFailureAction =>
  createAction(CHECK_CONTACTS_FAILURE);

// Check Contact
export const checkContactRequest = (
  contactUuid: string,
  connectionId: string
): CheckContactRequestAction =>
  createAction(CHECK_CONTACT_REQUEST, { contactUuid, connectionId });

export const checkContactSuccess = (
  viesChecks: ViesCheck[],
  contact: Contact
): CheckContactSuccessAction =>
  createAction(CHECK_CONTACT_SUCCESS, { viesChecks, contact });

export const checkContactFailure = (): CheckContactFailureAction =>
  createAction(CHECK_CONTACT_FAILURE);

// Refresh Contacts
export const refreshContactsRequest = (
  connectionId: string
): RefreshContactsRequestAction =>
  createAction(REFRESH_CONTACTS_REQUEST, { connectionId });

export const refreshContactsSuccess = (): RefreshContactsSuccessAction =>
  createAction(REFRESH_CONTACTS_SUCCESS);

export const refreshContactsFailure = (): RefreshContactsFailureAction =>
  createAction(REFRESH_CONTACTS_FAILURE);

// Refresh Contact
export const refreshContactRequest = (
  contactId: string,
  connectionId: string
): RefreshContactRequestAction =>
  createAction(REFRESH_CONTACT_REQUEST, { contactId, connectionId });

export const refreshContactSuccess = (): RefreshContactSuccessAction =>
  createAction(REFRESH_CONTACT_SUCCESS);

export const refreshContactFailure = (): RefreshContactFailureAction =>
  createAction(REFRESH_CONTACT_FAILURE);

// Hide Contact
export const hideContactRequest = (
  hideContactData: HideContactData,
  connectionId
): HideContactRequestAction =>
  createAction(HIDE_CONTACT_REQUEST, { hideContactData, connectionId });

export const hideContactSuccess = (
  hideContactData: HideContactData
): HideContactSuccessAction =>
  createAction(HIDE_CONTACT_SUCCESS, { hideContactData });

export const hideContactFailure = (): HideContactFailureAction =>
  createAction(HIDE_CONTACT_FAILURE);

// Remove Vies Info
export const removeViesInfo = (connectionId: string): RemoveViesInfoAction =>
  createAction(REMOVE_VIES_INFO, { connectionId });

// Reset
export const resetVatCheckerData = (): ResetVatCheckerDataAction =>
  createAction(RESET_VAT_CHECKER_DATA);
